// ============================================================================
// GlossaryContent — shared renderer for the stats glossary.
// Used by pages/Glossary.js (full page) and components/FloatingGlossary.js
// (Cmd/Ctrl+G dialog) so the two surfaces never drift apart.
// Data lives in ./glossaryData.js — the single source of truth.
// ============================================================================
import React, { useMemo, useRef, useState } from 'react';
import {
  Box, Typography, TextField, InputAdornment, Chip, Stack, Card, CardContent,
  Accordion, AccordionSummary, AccordionDetails, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Divider, useTheme, alpha,
} from '@mui/material';
import {
  Search, ExpandMore, Science, Assessment, SportsBaseball, ShowChart,
  AccountBalance, TrendingUp, Groups, Radar,
} from '@mui/icons-material';
import { GLOSSARY_SECTIONS, GLOSSARY_QUICK_CHIPS, GLOSSARY_FOOTNOTE } from './glossaryData';

const SECTION_ICONS = {
  science: <Science />,
  assessment: <Assessment />,
  batting: <SportsBaseball />,
  radar: <Radar />,
  chart: <ShowChart />,
  bank: <AccountBalance />,
  trending: <TrendingUp />,
  groups: <Groups />,
};

// Short labels for the sticky section nav.
const NAV_LABELS = {
  proprietary: 'The Cycle',
  war: 'WAR',
  'advanced-batting': 'Batting',
  'batted-ball': 'Batted Ball',
  'advanced-pitching': 'Pitching',
  contract: 'Contract',
  projections: 'Projections',
  team: 'Team',
};

const StatCard = ({ stat, isLast }) => {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        mb: isLast ? 0 : 2,
        bgcolor: alpha(theme.palette.primary.main, 0.02),
        border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
        borderRadius: 2,
      }}
    >
      <CardContent sx={{ p: { xs: 1.75, sm: 2.5 }, '&:last-child': { pb: { xs: 1.75, sm: 2.5 } } }}>
        {/* Stat header */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
          <Typography variant="h6" fontWeight={800} sx={{ fontFamily: 'monospace', fontSize: { xs: '1.05rem', sm: '1.2rem' } }}>
            {stat.name}
          </Typography>
          <Chip label={stat.scale} size="small" sx={{ fontWeight: 600, fontSize: '0.75rem', height: 22 }} />
          <Chip label={stat.applies} size="small" variant="outlined" color="primary" sx={{ fontWeight: 600, fontSize: '0.72rem', height: 20 }} />
        </Stack>

        <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1, fontSize: '0.85rem' }}>
          {stat.fullName}
        </Typography>

        <Typography variant="body1" sx={{ mb: 1.5, fontSize: { xs: '0.9rem', sm: '0.95rem' }, lineHeight: 1.6 }}>
          {stat.description}
        </Typography>

        {/* Formula */}
        {stat.formula && (
          <Box sx={{
            mb: 1.5, p: 1.5, borderRadius: 1.5,
            bgcolor: alpha(theme.palette.text.primary, 0.04),
            fontFamily: 'monospace', fontSize: { xs: '0.75rem', sm: '0.82rem' },
            whiteSpace: 'pre-wrap', lineHeight: 1.65, overflowX: 'auto',
          }}>
            {stat.formula}
          </Box>
        )}

        {/* Components table */}
        {stat.components && stat.components.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 0.5, display: 'block', letterSpacing: '0.05em' }}>
              COMPONENTS
            </Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Component</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Weight</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stat.components.map((c) => (
                    <TableRow key={c.name}>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{c.name}</TableCell>
                      <TableCell align="center" sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{c.max}</TableCell>
                      <TableCell sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>{c.desc}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Tiers */}
        {stat.tiers && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 0.5, display: 'block', letterSpacing: '0.05em' }}>
              TIERS
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {stat.tiers.map((t) => (
                <Chip
                  key={t.label}
                  label={`${t.label} (${t.range})`}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 600, fontSize: '0.75rem', height: 24, bgcolor: alpha(t.color, 0.12), color: t.color, borderColor: alpha(t.color, 0.3) }}
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* Benchmarks */}
        {stat.benchmarks && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 0.5, display: 'block', letterSpacing: '0.05em' }}>
              BENCHMARKS
            </Typography>
            <Stack spacing={0.35}>
              {stat.benchmarks.map((b) => (
                <Typography key={b.label} variant="body2" sx={{ fontSize: '0.85rem' }}>
                  <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, minWidth: 64, display: 'inline-block' }}>{b.label}</Box>
                  {' '}<Box component="span" color="text.secondary">{b.desc}</Box>
                </Typography>
              ))}
            </Stack>
          </Box>
        )}

        {/* Details */}
        {stat.details && (
          <Box sx={{ mb: stat.teamNote ? 1.5 : 0 }}>
            {stat.details.map((d, i) => (
              <Typography key={i} variant="body2" sx={{
                fontSize: '0.85rem', color: 'text.secondary', mb: 0.4, pl: 1.75,
                position: 'relative', lineHeight: 1.55,
                '&::before': { content: '"•"', position: 'absolute', left: 2 },
              }}>
                {d}
              </Typography>
            ))}
          </Box>
        )}

        {/* Team note */}
        {stat.teamNote && (
          <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: alpha(theme.palette.info.main, 0.06), border: `1px solid ${alpha(theme.palette.info.main, 0.15)}` }}>
            <Typography variant="caption" fontWeight={700} color="info.main" sx={{ display: 'block', mb: 0.25, letterSpacing: '0.05em' }}>
              TEAM APPLICATION
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{stat.teamNote}</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * @param {Object} props
 * @param {'page'|'dialog'} props.variant — page shows larger paddings; dialog is denser.
 * @param {{current: HTMLElement|null}} [props.scrollContainerRef] — ref to the scrolling element (DialogContent); defaults to window.
 * @param {string} [props.initialSearch]
 */
const GlossaryContent = ({ variant = 'page', scrollContainerRef = null, initialSearch = '' }) => {
  const theme = useTheme();
  const [search, setSearch] = useState(initialSearch);
  // Multi-expand: users kept losing their place with single-expand accordions.
  const [expandedIds, setExpandedIds] = useState(() => new Set(['proprietary']));
  const sectionRefs = useRef({});

  const filteredSections = useMemo(() => {
    if (search.length < 2) return GLOSSARY_SECTIONS;
    const q = search.toLowerCase();
    return GLOSSARY_SECTIONS.map(section => ({
      ...section,
      stats: section.stats.filter(stat =>
        stat.name.toLowerCase().includes(q) ||
        stat.fullName.toLowerCase().includes(q) ||
        stat.description.toLowerCase().includes(q)
      ),
    })).filter(section => section.stats.length > 0);
  }, [search]);

  const searching = search.length >= 2;

  const toggleSection = (id, expanded) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (expanded) next.add(id); else next.delete(id);
      return next;
    });
  };

  const scrollToSection = (id) => {
    setExpandedIds(prev => new Set(prev).add(id));
    // Wait for the accordion to expand before scrolling.
    requestAnimationFrame(() => {
      const el = sectionRefs.current[id];
      if (!el) return;
      const container = scrollContainerRef?.current;
      if (container) {
        const top = el.offsetTop - 56; // clear the sticky nav
        container.scrollTo({ top, behavior: 'smooth' });
      } else {
        const y = el.getBoundingClientRect().top + window.scrollY - 120;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    });
  };

  return (
    <Box>
      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search stats… (e.g. CVR, Surplus, FIP)"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><Search sx={{ fontSize: 20, color: 'text.secondary' }} /></InputAdornment>
          ),
        }}
        sx={{ mb: 1.5, maxWidth: variant === 'page' ? 480 : '100%' }}
      />

      {/* Quick chips */}
      {!search && (
        <Stack direction="row" spacing={0.5} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
          {GLOSSARY_QUICK_CHIPS.map(stat => (
            <Chip
              key={stat}
              label={stat}
              size="small"
              variant="outlined"
              onClick={() => setSearch(stat)}
              sx={{ cursor: 'pointer', fontWeight: 600, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) } }}
            />
          ))}
        </Stack>
      )}

      {/* Sticky section nav */}
      {!searching && (
        <Box sx={{
          position: 'sticky',
          top: variant === 'dialog' ? 0 : { xs: 56, sm: 64 },
          zIndex: 5,
          py: 0.75,
          mb: 1.5,
          mx: -0.5, px: 0.5,
          bgcolor: 'background.paper',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
          display: 'flex',
          gap: 0.5,
          overflowX: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}>
          {GLOSSARY_SECTIONS.map(s => (
            <Chip
              key={s.id}
              label={NAV_LABELS[s.id] || s.title}
              size="small"
              onClick={() => scrollToSection(s.id)}
              sx={{
                cursor: 'pointer', fontWeight: 600, flexShrink: 0,
                bgcolor: expandedIds.has(s.id) ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            />
          ))}
        </Box>
      )}

      {/* Sections */}
      {filteredSections.map((section) => (
        <Accordion
          key={section.id}
          ref={(el) => { sectionRefs.current[section.id] = el; }}
          expanded={searching || expandedIds.has(section.id)}
          onChange={(_, expanded) => toggleSection(section.id, expanded)}
          elevation={0}
          sx={{
            mb: 1.5,
            borderRadius: '12px !important',
            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            '&:before': { display: 'none' },
            overflow: 'hidden',
          }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ color: theme.palette.primary.main, display: 'flex' }}>{SECTION_ICONS[section.iconKey]}</Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>{section.title}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                  {section.description}
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, px: { xs: 1.25, sm: 2 } }}>
            {section.stats.map((stat, idx) => (
              <StatCard key={stat.name} stat={stat} isLast={idx === section.stats.length - 1} />
            ))}
          </AccordionDetails>
        </Accordion>
      ))}

      {filteredSections.length === 0 && (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
          No stats match “{search}”.
        </Typography>
      )}

      {/* Footer */}
      <Divider sx={{ my: 2.5 }} />
      <Typography variant="body2" color="text.secondary" align="center" sx={{ pb: 1.5, fontSize: '0.82rem' }}>
        {GLOSSARY_FOOTNOTE}
      </Typography>
    </Box>
  );
};

export default GlossaryContent;
